import { useCallback, useEffect, useState } from 'react';
import { IProxy } from '@tool-no-hu/shared/types';
import {
  checkMultipleProxiesHealthService,
  checkProxyHealthService,
  createProxyService,
  createManyProxiesService,
  updateProxyService,
  deleteProxyService,
  getProxiesService,
  getProxiesPaginatedService,
} from '../service/proxy';

export interface ProxyHealthResult {
  status: 'Live' | 'Dead';
  live: boolean;
}

export type ProxyHealthMap = Record<string, ProxyHealthResult>;

export interface UseProxiesResult {
  proxies: IProxy[];
  loading: boolean;
  submitting: boolean;
  checking: boolean;
  checkingId: string | null;
  healthResults: ProxyHealthMap;
  addProxy: (value: string) => Promise<boolean>;
  addManyProxies: (values: string[]) => Promise<boolean>;
  updateProxy: (id: string, value: string) => Promise<boolean>;
  deleteProxy: (id: string) => Promise<boolean>;
  checkAllHealth: () => Promise<void>;
  checkSingleHealth: (value: string) => Promise<void>;
  refresh: () => Promise<void>;
  clearHealthResults: () => void;
  // Pagination + search
  page: number;
  totalPages: number;
  total: number;
  search: string;
  setPage: (p: number) => void;
  setSearch: (s: string) => void;
}

export function useProxies(): UseProxiesResult {
  const [proxies, setProxies] = useState<IProxy[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [checking, setChecking] = useState(false);
  const [checkingId, setCheckingId] = useState<string | null>(null);
  const [healthResults, setHealthResults] = useState<ProxyHealthMap>({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getProxiesPaginatedService(page, 10, search || undefined);
      setProxies(result.items);
      setTotalPages(result.totalPages);
      setTotal(result.total);
    } catch (error) {
      console.error('Failed to load proxies list:', error);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const addProxy = useCallback(
    async (value: string): Promise<boolean> => {
      const trimmed = value.trim();
      if (!trimmed || submitting) return false;
      setSubmitting(true);
      try {
        const proxy = await createProxyService(trimmed);
        if (proxy) {
          await refresh();
          return true;
        }
        return false;
      } catch (error) {
        console.error('Failed to create proxy:', error);
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [refresh, submitting],
  );

  const addManyProxies = useCallback(
    async (values: string[]): Promise<boolean> => {
      if (values.length === 0 || submitting) return false;
      setSubmitting(true);
      try {
        const created = await createManyProxiesService(values);
        if (created.length > 0) {
          await refresh();
          return true;
        }
        return false;
      } catch (error) {
        console.error('Failed to bulk create proxies:', error);
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [refresh, submitting],
  );

  const updateProxy = useCallback(
    async (id: string, value: string): Promise<boolean> => {
      if (!id || !value) return false;
      try {
        const updated = await updateProxyService(id, value);
        if (updated) {
          setProxies((prev) => prev.map((p) => p._id === id ? { ...p, ...updated } : p));
          return true;
        }
        return false;
      } catch (error) {
        console.error('Failed to update proxy:', error);
        return false;
      }
    },
    [],
  );

  const deleteProxy = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const success = await deleteProxyService(id);
        if (success) {
          setProxies((prev) => prev.filter((p) => p._id !== id));
          setHealthResults((prev) => {
            const next: ProxyHealthMap = { ...prev };
            Reflect.deleteProperty(next, id);
            return next;
          });
          return true;
        }
        return false;
      } catch (error) {
        console.error('Failed to delete proxy:', error);
        return false;
      }
    },
    [],
  );

  const checkAllHealth = useCallback(async () => {
    if (proxies.length === 0 || checking) return;
    setChecking(true);
    try {
      const values = proxies
        .map((p) => p.value)
        .filter((v): v is string => Boolean(v));
      if (values.length === 0) {
        setChecking(false);
        return;
      }
      await checkMultipleProxiesHealthService(values);
      // Kết quả đã được persist vào DB — reload lại list
      await refresh();
    } catch (error) {
      console.error('Failed to execute bulk proxy health check:', error);
    } finally {
      setChecking(false);
    }
  }, [proxies, checking, refresh]);

  const checkSingleHealth = useCallback(async (value: string) => {
    if (!value || checkingId) return;
    setCheckingId(value);
    try {
      const alive = await checkProxyHealthService(value);
      setProxies((prev) => prev.map((p) =>
        p.value === value ? { ...p, alive, lastChecked: new Date() } : p
      ));
    } catch (error) {
      console.error('Failed to check proxy health:', error);
    } finally {
      setCheckingId(null);
    }
  }, [checkingId]);

  const clearHealthResults = useCallback(() => {
    setHealthResults({});
  }, []);

  const handleSetSearch = useCallback((s: string) => {
    setSearch(s);
    setPage(1);
  }, []);

  return {
    proxies,
    loading,
    submitting,
    checking,
    checkingId,
    healthResults,
    addProxy,
    addManyProxies,
    updateProxy,
    deleteProxy,
    checkAllHealth,
    checkSingleHealth,
    refresh,
    clearHealthResults,
    page,
    totalPages,
    total,
    search,
    setPage,
    setSearch: handleSetSearch,
  };
}
